import { HTTP_STATUS } from "@servicepro/utils";
/**
 * Submit a lead, and do not lose it if the API is down (SP-400). Every jobsite
 * form funnels through here so the fallback is a property of submitting rather
 * than a rule each new form must remember. During a real outage the browser
 * usually sees a rejected promise, not a status: Cloudflare's 502 page carries
 * no CORS headers and the API's custom-domain check fails closed. So `catch` is
 * the primary detector and `!res.ok` the secondary one; both are load-bearing.
 */

type SubmitLeadStatus = "ok" | "backed-up" | "failed";

interface SubmitLeadResult {
  status: SubmitLeadStatus;
  /** Absent on a clean primary submit. */
  reason?: BackupReason;
  /** The API's own words on a 4xx, for callers that show them (WebMCP). */
  errorMessage?: string;
}

type BackupReason = "primary_5xx" | "primary_network" | "primary_timeout";

interface SubmitLeadOptions {
  apiUrl: string;
  siteId: string;
  /** Named in the outage alert, so Slack says who lost a lead rather than a uuid. */
  businessName?: string;
  /** Absent simply disables the fallback. */
  backupApiUrl?: string;
  payload: Record<string, unknown>;
  /** CF answers 502 fast, but an origin mid-restart can hang instead. */
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}

const DEFAULT_TIMEOUT_MS = 8000,

  /** A DOMException from AbortSignal.timeout, distinguished from a network drop. */
  isTimeout = (err: unknown): boolean => (
    typeof err === "object" && err !== null && (err as { name?: string }).name === "TimeoutError"
  ),

  /** What the API said about a 4xx, when it said anything parseable. */
  rejectionMessage = async (res: Response): Promise<string | undefined> => {
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown },
        text = typeof body.message === "string" ? body.message : body.error;
      if (typeof text === "string" && text.trim()) {
        return text;
      }
      return undefined;
    } catch {
      return undefined;
    }
  },

  timeoutSignal = (ms: number): AbortSignal | undefined => {
    // Safari < 16 has neither; the request then runs without a deadline.
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
      return AbortSignal.timeout(ms);
    }
    return undefined;
  },

  submitLead = async (options: SubmitLeadOptions): Promise<SubmitLeadResult> => {
    const {
      apiUrl,
      siteId,
      backupApiUrl,
      businessName,
      payload,
      timeoutMs = DEFAULT_TIMEOUT_MS,
      fetchImpl = fetch,
    } = options;

    let reason: BackupReason;

    try {
      const res = await fetchImpl(`${apiUrl}/api/sites/${siteId}/forms/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: timeoutSignal(timeoutMs),
      });

      if (res.ok) {
        return { status: "ok" };
      }

      // A 4xx is a decision, not an outage; backing it up would bypass the defense just applied.
      if (res.status < HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        return { errorMessage: await rejectionMessage(res), status: "failed" };
      }

      reason = "primary_5xx";
    } catch (error) {
      reason = isTimeout(error) ? "primary_timeout" : "primary_network";
    }

    if (!backupApiUrl) {
      return { reason, status: "failed" };
    }

    // Single-use and already spent; parking a bot-protection credential buys nothing.
    const { turnstileToken: _spent, ...forBackup } = payload;

    try {
      const res = await fetchImpl(`${backupApiUrl}/capture/${siteId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...forBackup,
          __backupReason: reason,
          ...(businessName ? { __businessName: businessName } : {}),
        }),
        signal: timeoutSignal(timeoutMs),
      });

      // The backup answers 503 when its own write fails, so non-ok means the lead is nowhere.
      if (res.ok) {
        return { reason, status: "backed-up" };
      }
      return { reason, status: "failed" };
    } catch {
      return { reason, status: "failed" };
    }
  };

export type { SubmitLeadStatus, SubmitLeadResult, BackupReason, SubmitLeadOptions };
export { submitLead };
